import { useState, useEffect } from "react";
import {
    Eye, ThumbsUp, MessageSquare, Clock, TrendingUp, BarChart3,
    ArrowUpRight, ArrowDownRight, Globe, Monitor, Smartphone, Tablet, Tv,
    Share2, PlayCircle, CalendarDays,
} from "lucide-react";

import { useTranslation } from 'react-i18next';
/* ── Overview: line chart data (multiple series) ── */
const watchTimePts = [5, 12, 8, 45, 55, 35, 40, 30, 38, 32, 35, 38];
const subscriberPts = [0, 0, 1, 2, 3, 5, 6, 7, 8, 9, 11, 14];
const viewsPts = [2, 8, 15, 20, 35, 50, 55, 65, 70, 80, 85, 95];

/* ── Audience data ── */
const trafficSources = [
    { name: "Shorts", count: "19,622", pct: 96.6, color: "bg-purple-500" },
    { name: "Yt Search", count: "246", pct: 1.2, color: "bg-pink-500" },
    { name: "Yt Channel", count: "97", pct: 0.5, color: "bg-blue-500" },
    { name: "Direct", count: "331", pct: 1.6, color: "bg-green-500" },
];

const devices = [
    { name: "Mobile", icon: <Smartphone className="size-2.5" />, pct: 79.7, color: "bg-pink-500" },
    { name: "Desktop", icon: <Monitor className="size-2.5" />, pct: 8.5, color: "bg-green-500" },
    { name: "Tablet", icon: <Tablet className="size-2.5" />, pct: 6.9, color: "bg-blue-500" },
    { name: "TV", icon: <Tv className="size-2.5" />, pct: 4.9, color: "bg-yellow-500" },
];

const countries = [
    { code: "US", pct: 54.8, views: "8,412" },
    { code: "IN", pct: 12.7, views: "1,948" },
    { code: "GB", pct: 12.5, views: "1,922" },
    { code: "CA", pct: 5.5, views: "848" },
];

/* ── Content Intelligence data ── */
const statCards = [
    { icon: <Eye className="size-3" />, label: "Total Views", display: "16.8K", bg: "bg-blue-50", iconBg: "bg-blue-100", iconColor: "text-blue-600" },
    { icon: <ThumbsUp className="size-3" />, label: "Total Likes", display: "143", bg: "bg-pink-50", iconBg: "bg-pink-100", iconColor: "text-pink-600" },
    { icon: <MessageSquare className="size-3" />, label: "Comments", display: "24", bg: "bg-green-50", iconBg: "bg-green-100", iconColor: "text-green-600" },
    { icon: <Clock className="size-3" />, label: "Watch Time", display: "1,919m", bg: "bg-cyan-50", iconBg: "bg-cyan-100", iconColor: "text-cyan-600" },
    { icon: <TrendingUp className="size-3" />, label: "Avg Engagement", display: "0.9%", bg: "bg-purple-50", iconBg: "bg-purple-100", iconColor: "text-purple-600" },
    { icon: <BarChart3 className="size-3" />, label: "Published", display: "10", bg: "bg-orange-50", iconBg: "bg-orange-100", iconColor: "text-orange-600" },
];

/* ── Engagement data ── */
const engagementCards = [
    { icon: <ThumbsUp className="size-3" />, label: "Dislikes", value: "5", sub: "", iconColor: "text-red-500", iconBg: "bg-red-50" },
    { icon: <Eye className="size-3" />, label: "Engaged Views", value: "0", sub: "Watched past initial threshold", iconColor: "text-green-500", iconBg: "bg-green-50" },
    { icon: <PlayCircle className="size-3" />, label: "YouTube Premium", value: "0", sub: "No Premium views this period", iconColor: "text-purple-500", iconBg: "bg-purple-50" },
];

const sharingPlatforms = [
    { name: "WhatsApp", pct: 63.6, count: 7, color: "bg-red-400" },
    { name: "Other", pct: 27.3, count: 3, color: "bg-pink-400" },
    { name: "Text Message", pct: 9.1, count: 1, color: "bg-orange-400" },
];

const playbackLocations = [
    { name: "Shorts Feed", pct: 99.9, views: "20,298", color: "bg-purple-500" },
    { name: "Watch Page", pct: 0.1, views: "11", color: "bg-blue-400" },
];

/* ── Helpers ── */
const buildSvgPath = (points: number[], progress: number, w: number, h: number) => {
    const active = points.slice(0, progress);
    if (active.length < 2) return { line: "", area: "" };
    const maxVal = Math.max(...points, 1);
    const stepX = w / (points.length - 1);
    const coords = active.map((p, i) => ({ x: i * stepX, y: h - (p / maxVal) * h * 0.85 }));

    let line = `M ${coords[0]!.x},${coords[0]!.y}`;
    for (let i = 1; i < coords.length; i++) {
        const prev = coords[i - 1]!;
        const curr = coords[i]!;
        const cpx = (prev.x + curr.x) / 2;
        line += ` C ${cpx},${prev.y} ${cpx},${curr.y} ${curr.x},${curr.y}`;
    }
    const last = coords[coords.length - 1]!;
    const area = `${line} L ${last.x},${h} L ${coords[0]!.x},${h} Z`;
    return { line, area };
};

const dateLabels = ["Feb 27", "Mar 1", "Mar 5", "Mar 7", "Mar 11"];

/* ── Sub-tab type ── */
type SubTab = "overview" | "audience" | "engagement" | "intelligence";

export const AnimatedAnalyticsPreview = () => {
    const { t } = useTranslation();
    const [activeSubTab, setActiveSubTab] = useState<SubTab>("overview");
    const [chartProgress, setChartProgress] = useState(0);
    const [barsVisible, setBarsVisible] = useState(0);
    const [statsVisible, setStatsVisible] = useState(0);

    // Cycle through sub-tabs automatically
    useEffect(() => {
        const order: SubTab[] = ["overview", "audience", "engagement", "intelligence"];
        let idx = 0;
        const interval = setInterval(() => {
            idx = (idx + 1) % order.length;
            setActiveSubTab(order[idx]!);
        }, 4000);
        return () => clearInterval(interval);
    }, []);

    // Animate content when sub-tab changes
    useEffect(() => {
        setChartProgress(0);
        setBarsVisible(0);
        setStatsVisible(0);

        const timers: ReturnType<typeof setTimeout>[] = [];

        if (activeSubTab === "overview") {
            for (let i = 1; i <= 12; i++) {
                timers.push(setTimeout(() => setChartProgress(i), i * 120));
            }
        } else if (activeSubTab === "audience" || activeSubTab === "engagement") {
            for (let i = 1; i <= 6; i++) {
                timers.push(setTimeout(() => setBarsVisible(i), 200 + i * 200));
            }
        } else {
            for (let i = 1; i <= 6; i++) {
                timers.push(setTimeout(() => setStatsVisible(i), 100 + i * 150));
            }
        }

        return () => timers.forEach(clearTimeout);
    }, [activeSubTab]);

    const subTabConfig: { id: SubTab; label: string }[] = [
        { id: "overview", label: "Overview" },
        { id: "audience", label: "Audience" },
        { id: "engagement", label: "Engagement" },
        { id: "intelligence", label: "Content Intelligence" },
    ];

    return (
        <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden text-[10px] h-full">
            {/* ── Red gradient header ── */}
            <div className="bg-gradient-to-r from-red-400 to-red-500 px-3 py-2">
                <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-1.5">
                        <div className="w-5 h-5 bg-white/20 rounded-full flex items-center justify-center text-white text-[7px] font-bold">PW</div>
                        <div>
                            <div className="text-white font-semibold text-[9px]">Plan Wise</div>
                            <div className="text-white/70 text-[6px]">@planwisesocial</div>
                        </div>
                    </div>
                    <div className="flex gap-2 text-white/80 text-[7px]">
                        <span>11 SUBSCRIBERS</span>
                        <span>27 VIDEOS</span>
                    </div>
                </div>
                {/* Header stat boxes */}
                <div className="grid grid-cols-4 gap-1">
                    {[
                        { label: "Total Watch Time", value: "1,919" },
                        { label: "Avg View Duration", value: "21s" },
                        { label: "Avg View %", value: "321.7%" },
                        { label: "Subscribers", value: "+14" },
                    ].map((s) => (
                        <div key={s.label} className="bg-white/15 rounded-lg px-2 py-1.5">
                            <div className="text-white/70 text-[6px]">{s.label}</div>
                            <div className="text-white font-bold text-xs">{s.value}</div>
                        </div>
                    ))}
                </div>
            </div>

            {/* ── Sub-tab navigation ── */}
            <div className="flex justify-center py-2 border-b border-gray-100">
                <div className="inline-flex bg-red-50 rounded-full p-0.5 gap-0.5">
                    {subTabConfig.map((t) => (
                        <button
                            key={t.id}
                            onClick={() => setActiveSubTab(t.id)}
                            className={`px-2 py-0.5 rounded-full text-[7px] font-medium transition-all duration-300 whitespace-nowrap ${activeSubTab === t.id
                                ? "bg-white text-red-500 shadow-sm"
                                : "text-red-400 hover:text-red-500"
                                }`}
                        >
                            {t.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* ── Content area ── */}
            <div className="p-3 space-y-2 min-h-[260px]">
                {/* ═══ OVERVIEW ═══ */}
                {activeSubTab === "overview" && (
                    <div className="space-y-2 animate-fadeInUp">
                        {/* Section title */}
                        <div className="flex items-center gap-1">
                            <BarChart3 className="size-3 text-red-400" />
                            <span className="font-semibold text-gray-700 text-[9px]">{t('landing.mock.performanceTrends')}</span>
                            <span className="text-[6px] text-gray-400 ml-auto">Last 30 days · 12 snapshots</span>
                        </div>

                        {/* Watch Time chart */}
                        <div className="border border-gray-100 rounded-lg p-2">
                            <div className="text-[8px] font-semibold text-gray-800">{t('landing.mock.watchTimeOverTime')}</div>
                            <div className="text-[6px] text-gray-400 mb-1">{t('landing.mock.totalMinutesWatched')}</div>
                            <svg viewBox="0 0 260 50" className="w-full h-11">
                                {[0, 16, 33, 50].map((y) => (
                                    <line key={y} x1="0" y1={y} x2="260" y2={y} stroke="#f3f4f6" strokeWidth="0.5" />
                                ))}
                                {(() => {
                                    const { line, area } = buildSvgPath(watchTimePts, chartProgress, 260, 50); return (<>
                                        {area && <path d={area} fill="url(#redFill)" opacity="0.2" />}
                                        {line && <path d={line} fill="none" stroke="#ef4444" strokeWidth="1.2" strokeLinecap="round" />}
                                    </>);
                                })()}
                                <defs>
                                    <linearGradient id="redFill" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor="#ef4444" stopOpacity="0.4" />
                                        <stop offset="100%" stopColor="#ef4444" stopOpacity="0" />
                                    </linearGradient>
                                </defs>
                            </svg>
                            <div className="flex justify-between text-[5px] text-gray-400 mt-0.5">
                                {dateLabels.map((d) => <span key={d}>{d}</span>)}
                            </div>
                        </div>

                        {/* Subscribers + Views side by side */}
                        <div className="grid grid-cols-2 gap-1.5">
                            <div className="border border-gray-100 rounded-lg p-2">
                                <div className="text-[8px] font-semibold text-gray-800">{t('landing.mock.subscribers')}</div>
                                <div className="text-[6px] text-gray-400 mb-1">{t('landing.mock.growthLoss')}</div>
                                <svg viewBox="0 0 120 40" className="w-full h-8">
                                    {(() => {
                                        const { line, area } = buildSvgPath(subscriberPts, chartProgress, 120, 40); return (<>
                                            {area && <path d={area} fill="url(#greenFill)" opacity="0.2" />}
                                            {line && <path d={line} fill="none" stroke="#22c55e" strokeWidth="1" strokeLinecap="round" />}
                                        </>);
                                    })()}
                                    <defs>
                                        <linearGradient id="greenFill" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="0%" stopColor="#22c55e" stopOpacity="0.4" />
                                            <stop offset="100%" stopColor="#22c55e" stopOpacity="0" />
                                        </linearGradient>
                                    </defs>
                                </svg>
                            </div>
                            <div className="border border-gray-100 rounded-lg p-2">
                                <div className="text-[8px] font-semibold text-gray-800">{t('landing.mock.impressions')}</div>
                                <div className="text-[6px] text-gray-400 mb-1">{t('landing.mock.totalViewsOverTime')}</div>
                                <svg viewBox="0 0 120 40" className="w-full h-8">
                                    {(() => {
                                        const { line, area } = buildSvgPath(viewsPts, chartProgress, 120, 40); return (<>
                                            {area && <path d={area} fill="url(#purpleFill)" opacity="0.2" />}
                                            {line && <path d={line} fill="none" stroke="#8b5cf6" strokeWidth="1" strokeLinecap="round" />}
                                        </>);
                                    })()}
                                    <defs>
                                        <linearGradient id="purpleFill" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="0%" stopColor="#8b5cf6" stopOpacity="0.4" />
                                            <stop offset="100%" stopColor="#8b5cf6" stopOpacity="0" />
                                        </linearGradient>
                                    </defs>
                                </svg>
                            </div>
                        </div>
                    </div>
                )}

                {/* ═══ AUDIENCE ═══ */}
                {activeSubTab === "audience" && (
                    <div className="space-y-2 animate-fadeInUp">
                        {/* Traffic Sources */}
                        <div className="border border-gray-100 rounded-lg p-2">
                            <div className="flex items-center gap-1 mb-1.5">
                                <TrendingUp className="size-3 text-purple-500" />
                                <span className="font-semibold text-gray-700 text-[9px]">{t('landing.mock.trafficSources')}</span>
                            </div>
                            <div className="space-y-1.5">
                                {trafficSources.map((src, i) => (
                                    <div key={src.name} className={`transition-all duration-500 ${i < barsVisible ? "opacity-100" : "opacity-0"}`}>
                                        <div className="flex justify-between items-center mb-0.5">
                                            <div className="flex items-center gap-1">
                                                <span className={`w-1.5 h-1.5 rounded-full ${src.color}`} />
                                                <span className="text-gray-700 font-medium">{src.name}</span>
                                                <span className="text-gray-400 text-[7px]">{src.count}</span>
                                            </div>
                                            <span className="font-semibold text-gray-600">{src.pct}%</span>
                                        </div>
                                        <div className="w-full bg-gray-100 rounded-full h-1">
                                            <div className={`${src.color} h-1 rounded-full transition-all duration-700`} style={{ width: i < barsVisible ? `${Math.min(src.pct, 100)}%` : "0%" }} />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Device Breakdown + Top Countries */}
                        <div className="grid grid-cols-2 gap-1.5">
                            <div className="border border-gray-100 rounded-lg p-2">
                                <div className="flex items-center gap-1 mb-1.5">
                                    <Monitor className="size-3 text-blue-500" />
                                    <span className="font-semibold text-gray-700 text-[8px]">{t('landing.mock.deviceBreakdown')}</span>
                                </div>
                                <div className="space-y-1.5">
                                    {devices.map((d, i) => (
                                        <div key={d.name} className={`transition-all duration-500 ${i < barsVisible ? "opacity-100" : "opacity-0"}`}>
                                            <div className="flex justify-between items-center mb-0.5">
                                                <div className="flex items-center gap-1">
                                                    {d.icon}
                                                    <span className="text-gray-700">{d.name}</span>
                                                </div>
                                                <span className="font-bold text-gray-800 text-[9px]">{d.pct}%</span>
                                            </div>
                                            <div className="w-full bg-gray-100 rounded-full h-0.5">
                                                <div className={`${d.color} h-0.5 rounded-full transition-all duration-700`} style={{ width: i < barsVisible ? `${d.pct}%` : "0%" }} />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div className="bg-gradient-to-br from-blue-100 to-blue-200 rounded-lg p-2">
                                <div className="flex items-center gap-1 mb-1.5">
                                    <Globe className="size-3 text-blue-600" />
                                    <span className="font-semibold text-blue-800 text-[8px]">{t('landing.mock.topCountries')}</span>
                                </div>
                                <div className="space-y-1.5">
                                    {countries.map((c, i) => (
                                        <div key={c.code} className={`transition-all duration-500 ${i < barsVisible ? "opacity-100" : "opacity-0"}`}>
                                            <div className="flex justify-between items-center mb-0.5">
                                                <div className="flex items-center gap-1">
                                                    <span className="text-[7px]">📍</span>
                                                    <span className="text-blue-800 font-medium">{c.code}</span>
                                                </div>
                                                <div className="text-right">
                                                    <span className="font-bold text-blue-900 text-[9px]">{c.pct}%</span>
                                                </div>
                                            </div>
                                            <div className="w-full bg-blue-300/30 rounded-full h-0.5">
                                                <div className="bg-blue-500 h-0.5 rounded-full transition-all duration-700" style={{ width: i < barsVisible ? `${c.pct}%` : "0%" }} />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* ═══ ENGAGEMENT ═══ */}
                {activeSubTab === "engagement" && (
                    <div className="space-y-2 animate-fadeInUp">
                        <div className="flex items-center gap-1">
                            <BarChart3 className="size-3 text-red-400" />
                            <span className="font-semibold text-gray-700 text-[9px]">{t('landing.mock.channelInsights')}</span>
                        </div>

                        {/* Stat cards row */}
                        <div className="grid grid-cols-3 gap-1">
                            {engagementCards.map((c, i) => (
                                <div key={c.label} className={`border border-gray-100 rounded-lg p-2 transition-all duration-500 ${i < barsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2"}`}>
                                    <div className={`w-4 h-4 ${c.iconBg} ${c.iconColor} rounded-md flex items-center justify-center mb-1`}>{c.icon}</div>
                                    <div className="text-[7px] text-gray-500">{c.label}</div>
                                    <div className="font-bold text-gray-900 text-sm">{c.value}</div>
                                    {c.sub && <div className="text-[6px] text-gray-400 mt-0.5">{c.sub}</div>}
                                </div>
                            ))}
                        </div>

                        {/* Sharing + Playback */}
                        <div className="grid grid-cols-2 gap-1.5">
                            <div className="border border-gray-100 rounded-lg p-2">
                                <div className="flex items-center gap-1 mb-1.5">
                                    <Share2 className="size-2.5 text-red-400" />
                                    <span className="font-semibold text-gray-700 text-[8px]">{t('landing.mock.sharingPlatforms')}</span>
                                </div>
                                <div className="space-y-1">
                                    {sharingPlatforms.map((s, i) => (
                                        <div key={s.name} className={`transition-all duration-500 ${i + 3 < barsVisible + 3 ? "opacity-100" : "opacity-0"}`}>
                                            <div className="flex justify-between mb-0.5">
                                                <span className="text-gray-600">{s.name}</span>
                                                <span className="font-semibold text-gray-700">{s.pct}%</span>
                                            </div>
                                            <div className="w-full bg-gray-100 rounded-full h-0.5">
                                                <div className={`${s.color} h-0.5 rounded-full transition-all duration-700`} style={{ width: `${s.pct}%` }} />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div className="border border-gray-100 rounded-lg p-2">
                                <div className="flex items-center gap-1 mb-1.5">
                                    <PlayCircle className="size-2.5 text-purple-500" />
                                    <span className="font-semibold text-gray-700 text-[8px]">{t('landing.mock.playbackLocations')}</span>
                                </div>
                                <div className="space-y-1">
                                    {playbackLocations.map((loc, i) => (
                                        <div key={loc.name} className={`transition-all duration-500 ${i + 3 < barsVisible + 3 ? "opacity-100" : "opacity-0"}`}>
                                            <div className="flex justify-between mb-0.5">
                                                <span className="text-gray-600">{loc.name}</span>
                                                <span className="font-bold text-gray-700">{loc.pct}%</span>
                                            </div>
                                            <div className="w-full bg-gray-100 rounded-full h-0.5">
                                                <div className={`${loc.color} h-0.5 rounded-full transition-all duration-700`} style={{ width: `${Math.min(loc.pct, 100)}%` }} />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* ═══ CONTENT INTELLIGENCE ═══ */}
                {activeSubTab === "intelligence" && (
                    <div className="space-y-2 animate-fadeInUp">
                        <div className="flex items-center gap-1">
                            <span className="text-[10px]">✨</span>
                            <span className="font-semibold text-gray-700 text-[9px] uppercase tracking-wide">{t('landing.mock.periodHighlights')}</span>
                            <span className="text-[6px] text-gray-400 ml-auto">{t('landing.mock.keyStats')}</span>
                        </div>

                        {/* 3×2 pastel stat grid */}
                        <div className="grid grid-cols-3 gap-1.5">
                            {statCards.map((s, i) => (
                                <div
                                    key={s.label}
                                    className={`${s.bg} rounded-lg p-2 transition-all duration-500 ${i < statsVisible ? "opacity-100 translate-y-0 scale-100" : "opacity-0 translate-y-3 scale-95"
                                        }`}
                                >
                                    <div className={`${s.iconBg} ${s.iconColor} w-4 h-4 rounded-md flex items-center justify-center mb-1`}>
                                        {s.icon}
                                    </div>
                                    <div className="font-bold text-gray-900 text-xs">{s.display}</div>
                                    <div className="text-[6px] text-gray-500">{s.label}</div>
                                </div>
                            ))}
                        </div>

                        {/* Best Performing & Needs Improvement */}
                        <div className="grid grid-cols-2 gap-1.5">
                            <div className={`bg-green-50 border border-green-100 rounded-lg p-2 transition-all duration-500 ${statsVisible >= 6 ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2"}`}>
                                <div className="flex items-center gap-0.5 text-green-600 text-[7px] font-semibold mb-0.5">
                                    <ArrowUpRight className="size-2.5" />{t('landing.mock.bestPerforming')}</div>
                                <div className="text-[8px] font-medium text-gray-800 truncate">Everyday Items You're Using Wrong</div>
                                <div className="text-[6px] text-gray-500">2.2K views</div>
                            </div>
                            <div className={`bg-red-50 border border-red-100 rounded-lg p-2 transition-all duration-500 ${statsVisible >= 6 ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2"}`} style={{ transitionDelay: "150ms" }}>
                                <div className="flex items-center gap-0.5 text-red-500 text-[7px] font-semibold mb-0.5">
                                    <ArrowDownRight className="size-2.5" />{t('landing.mock.needsImprovement')}</div>
                                <div className="text-[8px] font-medium text-gray-800 truncate">What was she wearing?</div>
                                <div className="text-[6px] text-gray-500">1.2K views</div>
                            </div>
                        </div>

                        {/* Best Upload Day & Time */}
                        <div className="grid grid-cols-2 gap-1.5">
                            <div className={`bg-indigo-50 border border-indigo-100 rounded-lg p-2 transition-all duration-500 ${statsVisible >= 6 ? "opacity-100" : "opacity-0"}`} style={{ transitionDelay: "250ms" }}>
                                <div className="flex items-center gap-0.5 text-indigo-600 text-[7px] font-semibold mb-0.5">
                                    <CalendarDays className="size-2.5" />{t('landing.mock.bestUploadDay')}</div>
                                <div className="text-[10px] font-bold text-gray-800">Tuesday</div>
                            </div>
                            <div className={`bg-purple-50 border border-purple-100 rounded-lg p-2 transition-all duration-500 ${statsVisible >= 6 ? "opacity-100" : "opacity-0"}`} style={{ transitionDelay: "350ms" }}>
                                <div className="flex items-center gap-0.5 text-purple-600 text-[7px] font-semibold mb-0.5">
                                    <Clock className="size-2.5" />{t('landing.mock.bestUploadTime')}</div>
                                <div className="text-[10px] font-bold text-gray-800">11 AM</div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
